<script setup lang="ts">
import TabContent from "~/components/TabContent.vue";
import TabChildren from "~/components/schema/TabChildren.vue";
import type SchemaChild from "~/types/SchemaChild";
import type SchemaParent from "~/types/SchemaParent";
import type Schema from "~/types/Schema";
import TabSchema from "~/components/schema/TabSchema.vue";
import { DATATYPES } from "~/constants/dataTypes";
import TabParent from "~/components/schema/TabParent.vue";
import TabVisualized from "~/components/schema/TabVisualized.vue";

const route = useRoute();
const router = useRouter();
// Get dataType base on router URL
const dataType = router.currentRoute.value.path.split("/")[1].toLowerCase();

const { tabs: tab2, apiEndpoint } = DATATYPES[dataType];
// Since tabs may be modified, we need to create a deep copy of it. This fix the back button issue.
const tabs = [...tab2];

const slug = (route.params.childSchema ?? "") as string;

const { data } = await useFetch(`${apiEndpoint}/${slug}`);
const schema = ref({} as Schema);
const children = ref([] as SchemaChild[]);
const parents = ref([] as SchemaParent[]);

if (data?.value) {
  schema.value = data.value?.fieldData ?? {};
  children.value = data.value?.portalData?.childPublished ?? [];
  parents.value = data.value?.portalData?.parentPublished ?? [];
}

if (children.value.length <= 0 && tabs.findIndex((tab) => tab.key === "children") >= 0) {
  tabs.splice(
    tabs.findIndex((tab) => tab.key === "children"),
    1,
  );
}
if (parents.value.length <= 0 && tabs.findIndex((tab) => tab.key === "parent") >= 0) {
  tabs.splice(
    tabs.findIndex((tab) => tab.key === "parent"),
    1,
  );
}
if (!schema.value?.jsonSchemaValidation && tabs.findIndex((tab) => tab.key === "schema") >= 0) {
  tabs.splice(
    tabs.findIndex((tab) => tab.key === "schema"),
    1,
  );
}
</script>

<template>
  <div>
    <div v-if="schema?.name">
      <div class="mb-2 md:mb-10">
        <h1 class="text-3xl capitalize leading-loose md:inline-block">{{ schema.name }}</h1>

        <p class="pl-1 font-light text-grc-gray-darker">{{ schema.description }}</p>
      </div>

      <TabContent :tabs="tabs" class="mt-6">
        <template #children>
          <TabChildren :schema="schema" :children="children" class="bg-white" />
        </template>

        <template #parent>
          <TabParent :schema="schema" :parents="parents" class="bg-white" />
        </template>

        <template #schema>
          <TabSchema :schema="schema" />
        </template>

        <template #visualized>
          <TabVisualized :schema="schema" />
        </template>
      </TabContent>
    </div>

    <div v-else class="py-4 text-center text-gray-500">Sorry, the requested page does not exist...</div>
  </div>
</template>

<style scoped></style>
